/**
 * BlankValueMatchTable (Phase 92, UI-SPEC 1.2 step 2; D-15, D-21, D-24).
 *
 * Matches each of the client's store sizes and colours to the blank's own
 * values. ONE `<table>` with a `<tbody>` row group per dimension ("Colours",
 * then "Sizes"), each with a `th scope="rowgroup"` header and a right-aligned
 * "{matched} of {total} matched" (DESIGN.md §12.1.1). One row per distinct
 * store VALUE, never per variant.
 *
 * - Auto-matched rows show the value, "Matched" and "Change match", which turns
 *   the cell into the picker.
 * - Every picker lists ONLY the blank's own values as `CODE · name` and ends
 *   with a separator and "Leave these variants out". No free text and no add
 *   row, ever (D-15): a Select up to 20 options, a Popover + Command Combobox
 *   above that (DESIGN.md §10), the plain shape from `SharedSkuPicker`, never
 *   `PrintSizeCodeCombobox`'s creatable row.
 * - Each colour row carries Light design / Dark design (D-24): pre-filled from
 *   the 3PL's suggestion with "Suggested by {owner}" under it, else Light with
 *   no hint. The client flips it freely and it never blocks the step.
 * - A product with no colour option (or one size) shows that group as one
 *   required prompt row.
 * - Enter on a picker moves focus down the "On the blank" column; past the
 *   last row it hands focus on (`onWalkPastEnd`, step 3), DESIGN.md §12.1.
 *
 * BLU-1522, grouped mode (`selection` and `grouped`): the client puts some
 * colours (or sizes) on another base garment of the same 3PL. A tick column
 * leads the rows of the split option, and every row names its base garment
 * (thumbnail and title, never a code). Each row carries its own garment's
 * values (`options`); pickers and matched values show names only. A row whose
 * garment has no preview yet (`pending`) shows a placeholder until it does; a
 * row whose garment has no values of that option (`noValues`) is settled.
 *
 * BLU-1522 S2b (`locked` rows): a row that carries a variant a sync already
 * wrote shows its value as text and "Already synced, can't change": no
 * picker, no "Change match", no tick box (and select-all skips it) and a
 * disabled light or dark switch.
 *
 * BLU-1522 S8 (`matchColumns={false}`): the own base garment editor lists
 * the rows only to tick them and show their base garment, so "On the blank",
 * the state, the design column and the matched count are hidden. A locked row
 * says "Already synced, can't change" under its garment instead.
 *
 * Option sources and suggestions come in as props, so the 3PL's Replace dialog
 * (`leaveOutLabel`: "Keep the current blank", `showDesignChoice={false}`,
 * `fromColumnLabel`: "Current blank", `leftOutStateLabel`: "Kept"), the client
 * composer for a custom product (`showDesignChoice={false}` only) and later
 * screens can feed it (D-21). With none of these props it renders exactly as
 * the client composer's table.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, ChevronsUpDown, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { StatusPill } from '@/components/shared/StatusPill';
import { cn } from '@/lib/utils';
import { GarmentThumb } from '../GarmentThumb';

/** DESIGN.md §10: a Select up to 20 options, a searchable Combobox above that. */
const SELECT_MAX_OPTIONS = 20;
const LEAVE_OUT = '__leave_out__';

export interface BlankValueOption {
  /** The code segment that lands in the print SKU. */
  code: string;
  name: string | null;
}

export type BlankValueMatchHow = 'auto' | 'chosen' | 'left_out' | 'unmatched';

export interface BlankValueMatchRow {
  /** The store value, or null for the one row of a product without that option. */
  storeValue: string | null;
  variantCount: number;
  /** The matched blank code, or null. */
  blankValue: string | null;
  how: BlankValueMatchHow;
  /** Colours only: the 3PL suggests a dark design for the matched colour (a pre-fill, D-24). */
  suggestedDark?: boolean;
  /** Grouped mode: the base garment this row is matched on. */
  garment?: { key: string; title: string; imageUrl: string | null };
  /** Grouped mode: this row's garment's own values, in place of the table's. */
  options?: BlankValueOption[];
  /** Grouped mode: the row's garment has no preview yet. */
  pending?: boolean;
  /** Grouped mode: the row's garment has no values of this option (an unsized tote): nothing to match. */
  noValues?: boolean;
  /** BLU-1522 S2b: the row carries a synced variant; its match, garment and light or dark cannot change. */
  locked?: boolean;
  /**
   * BLU-1522 S2b, colours: the colour has a synced variant on some base
   * garment, so its light or dark (one choice per colour) cannot change on
   * this row either, even when this row's own match can.
   */
  designLocked?: boolean;
}

export type BlankValueGroup = 'colour' | 'size';

/** Grouped mode: the option whose rows carry a tick box, and the ticked store values (trimmed, lower case). */
export interface BlankValueSelection {
  group: BlankValueGroup;
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}

export interface BlankValueMatchTableProps {
  /** Unique per table, for ids. */
  idPrefix: string;
  /** One row per distinct store value (the server's preview `matches`). */
  matches: { colours: BlankValueMatchRow[]; sizes: BlankValueMatchRow[] };
  /** The blank's own values, the only options offered (D-15). */
  blankValues: { colours: BlankValueOption[]; sizes: BlankValueOption[] };
  /** Light (false) or dark (true) per store colour: the client's choice, else the suggestion. */
  designFor: (storeColour: string | null) => boolean;
  /** The 3PL's name, for "Suggested by {owner}". */
  ownerName: string;
  /** `garmentKey` is the row's `garment.key` in grouped mode. */
  onMatch: (group: BlankValueGroup, storeValue: string | null, blankValue: string | null, garmentKey?: string) => void;
  onDesignChoice: (storeColour: string | null, dark: boolean) => void;
  /** Enter on the last picker; the composer moves focus to step 3. */
  onWalkPastEnd?: () => void;
  /** Replace for the last option's words (the 3PL's Replace flow: "Keep the current blank"). */
  leaveOutLabel?: string;
  /** Default true. False hides the light or dark column (the 3PL's Replace flow). */
  showDesignChoice?: boolean;
  /** Replace for the first column's header (the 3PL's Replace flow: "Current blank"). */
  fromColumnLabel?: string;
  /** Replace for the left-out state's words (the 3PL's Replace flow: "Kept"). */
  leftOutStateLabel?: string;
  /** BLU-1522: a tick box on each row of this option. */
  selection?: BlankValueSelection;
  /** BLU-1522: the Base garment column, and value names in place of codes. */
  grouped?: boolean;
  /**
   * BLU-1522 S8: default true. False hides "On the blank", the state, the
   * design column and the matched count (the own base garment editor: a row
   * is only ticked and moved, never matched).
   */
  matchColumns?: boolean;
}

function optionText(option: BlankValueOption, namesOnly = false): string {
  if (namesOnly) return option.name ?? option.code;
  return option.name ? `${option.code} · ${option.name}` : option.code;
}

/** A row's key, and the key a row is ticked by: its store value, trimmed and lower case. */
function rowKey(storeValue: string | null): string {
  return typeof storeValue === 'string' ? storeValue.trim().toLowerCase() : '';
}

export function BlankValueMatchTable({
  idPrefix,
  matches,
  blankValues,
  designFor,
  ownerName,
  onMatch,
  onDesignChoice,
  onWalkPastEnd,
  leaveOutLabel,
  showDesignChoice = true,
  fromColumnLabel,
  leftOutStateLabel,
  selection,
  grouped = false,
  matchColumns = true,
}: BlankValueMatchTableProps) {
  const { t } = useTranslation('productSync');
  const tableRef = useRef<HTMLTableElement>(null);
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  // "Change match" swaps the button for the picker; focus follows to the picker.
  const focusAfterRender = useRef<string | null>(null);
  useEffect(() => {
    if (!focusAfterRender.current) return;
    document.getElementById(focusAfterRender.current)?.focus();
    focusAfterRender.current = null;
  });
  const designColumn = matchColumns && showDesignChoice && matches.colours.length > 0;
  const columns = (matchColumns ? (designColumn ? 4 : 3) : 1) + (selection ? 1 : 0) + (grouped ? 1 : 0);
  const onBlankHeaderId = `${idPrefix}-on-blank`;

  const walk = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    // Enter walks; Space and the arrow keys still open the list.
    event.preventDefault();
    const controls = Array.from(
      tableRef.current?.querySelectorAll<HTMLElement>('[data-match-control]') ?? [],
    ).filter((el) => !el.hasAttribute('disabled'));
    const index = controls.indexOf(event.currentTarget);
    const next = controls[index + 1];
    if (next) next.focus();
    else onWalkPastEnd?.();
  };

  const groups: Array<{
    group: BlankValueGroup;
    title: string;
    rows: BlankValueMatchRow[];
    options: BlankValueOption[];
  }> = [
    { group: 'colour' as const, title: t('supply.match.colours'), rows: matches.colours, options: blankValues.colours },
    { group: 'size' as const, title: t('supply.match.sizes'), rows: matches.sizes, options: blankValues.sizes },
  ].filter((g) => g.rows.length > 0);

  const tickable = selection
    ? (groups.find((g) => g.group === selection.group)?.rows ?? []).filter(
        (row) => row.storeValue !== null && !row.locked,
      )
    : [];
  const tickedCount = selection ? tickable.filter((row) => selection.selected.has(rowKey(row.storeValue))).length : 0;
  const toggle = (key: string, on: boolean) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (on) next.add(key);
    else next.delete(key);
    selection.onChange(next);
  };

  return (
    <table ref={tableRef} className="w-full border-collapse text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          {selection && (
            <th scope="col" className="w-8 py-2 pr-2 font-medium">
              <Checkbox
                aria-label={
                  selection.group === 'colour' ? t('supply.match.selectAllColours') : t('supply.match.selectAllSizes')
                }
                checked={tickedCount === 0 ? false : tickedCount === tickable.length ? true : 'indeterminate'}
                disabled={tickable.length === 0}
                onCheckedChange={(next) =>
                  selection.onChange(next === true ? new Set(tickable.map((row) => rowKey(row.storeValue))) : new Set())
                }
              />
            </th>
          )}
          <th scope="col" className="py-2 pr-3 font-medium">
            {fromColumnLabel ?? t('supply.match.inStore')}
          </th>
          {grouped && (
            <th scope="col" className="py-2 pr-3 font-medium">
              {t('supply.match.baseGarment')}
            </th>
          )}
          {matchColumns && (
            <th scope="col" id={onBlankHeaderId} className="py-2 pr-3 font-medium">
              {t('supply.match.onBlank')}
            </th>
          )}
          {matchColumns && (
            <th scope="col" className="py-2 pr-3 font-medium">
              {t('supply.match.state')}
            </th>
          )}
          {designColumn && (
            <th scope="col" className="py-2 font-medium">
              {t('supply.match.design')}
            </th>
          )}
        </tr>
      </thead>
      {groups.map(({ group, title, rows, options }) => {
        const resolved = rows.filter((row) => row.how !== 'unmatched').length;
        // One row per garment in grouped mode, so every row may be the no-option prompt.
        const single = rows.length > 0 && rows.every((row) => row.storeValue === null);
        return (
          <tbody key={group}>
            <tr className="border-t bg-muted/30">
              <th scope="rowgroup" colSpan={columns} className="px-2 py-1.5 text-left text-xs font-medium">
                <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <span>{title}</span>
                  {matchColumns && (
                    <span className="font-normal tabular-nums text-muted-foreground">
                      {t('supply.match.count', { matched: resolved, total: rows.length })}
                    </span>
                  )}
                </span>
              </th>
            </tr>
            {rows.map((row, index) => {
              const key = `${group}:${row.garment?.key ?? ''}:${rowKey(row.storeValue)}`;
              const cellId = `${idPrefix}-${group}-${index}`;
              const helperId = `${cellId}-helper`;
              const locked = row.locked === true;
              const unmatched = row.how === 'unmatched' && !row.pending && !locked;
              const showPicker =
                !locked && !row.pending && !row.noValues && (row.how !== 'auto' || editing.has(key));
              const rowOptions = row.options ?? options;
              const matched = row.blankValue ? rowOptions.find((o) => o.code === row.blankValue) : undefined;
              const dark = group === 'colour' ? designFor(row.storeValue) : false;
              const tickHere = selection?.group === group && row.storeValue !== null && !locked;
              return (
                <tr key={key} className="border-t align-top" data-locked-row={locked ? 'true' : undefined}>
                  {selection && (
                    <td className="w-8 py-2 pr-2">
                      {tickHere && (
                        <Checkbox
                          aria-label={t('supply.match.selectRow', { value: row.storeValue ?? '' })}
                          checked={selection.selected.has(rowKey(row.storeValue))}
                          onCheckedChange={(next) => toggle(rowKey(row.storeValue), next === true)}
                        />
                      )}
                    </td>
                  )}
                  <td id={`${cellId}-store`} className="py-2 pr-3">
                    {single ? (
                      <span className="block max-w-xs leading-normal">
                        {group === 'colour' ? t('supply.match.noColourPrompt') : t('supply.match.oneSizePrompt')}
                      </span>
                    ) : (
                      <>
                        <span className="block break-words font-medium">{row.storeValue}</span>
                        <span className="block tabular-nums text-muted-foreground">
                          {t('supply.match.variants', { count: row.variantCount })}
                        </span>
                      </>
                    )}
                  </td>
                  {grouped && (
                    <td className="py-2 pr-3">
                      {row.garment && (
                        <span className="flex min-w-0 items-start gap-2" data-testid="supply-row-garment">
                          <GarmentThumb src={row.garment.imageUrl} size="sm" />
                          <span className="min-w-0 break-words leading-normal">{row.garment.title}</span>
                        </span>
                      )}
                      {!matchColumns && locked && (
                        <span className="mt-1 flex items-start gap-1 leading-normal text-muted-foreground">
                          <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                          {t('supply.match.syncedLocked')}
                        </span>
                      )}
                    </td>
                  )}
                  {matchColumns && (
                    <td className={cn('py-2 pr-3', grouped ? 'min-w-[8rem]' : 'min-w-[10rem]')}>
                      {row.pending ? (
                        <span className="flex items-center gap-2" data-testid="supply-row-pending">
                          <Skeleton className="h-8 w-28 motion-reduce:animate-none" aria-hidden="true" />
                          <span className="sr-only">{t('supply.match.checking')}</span>
                        </span>
                      ) : row.noValues ? (
                        <span className="break-words leading-normal text-muted-foreground" data-testid="supply-row-no-values">
                          {group === 'colour' ? t('supply.match.noColoursOnGarment') : t('supply.match.noSizesOnGarment')}
                        </span>
                      ) : locked ? (
                        <span className="break-words" data-testid="supply-row-locked-value">
                          {grouped ? (
                            matched ? optionText(matched, true) : row.blankValue
                          ) : matched ? (
                            <>
                              <span className="font-mono">{matched.code}</span>
                              {matched.name ? ` · ${matched.name}` : ''}
                            </>
                          ) : (
                            <span className="font-mono">{row.blankValue}</span>
                          )}
                        </span>
                      ) : showPicker ? (
                        <ValuePicker
                          id={`${cellId}-control`}
                          labelledBy={`${onBlankHeaderId} ${cellId}-store`}
                          options={rowOptions}
                          namesOnly={grouped}
                          value={row.how === 'left_out' ? LEAVE_OUT : row.blankValue}
                          leaveOutLabel={leaveOutLabel ?? t('supply.match.leaveOut')}
                          placeholder={group === 'colour' ? t('supply.match.chooseColour') : t('supply.match.chooseSize')}
                          invalid={unmatched}
                          describedBy={unmatched ? helperId : undefined}
                          state={row.how}
                          onKeyDown={walk}
                          onChange={(value) => {
                            const blankValue = value === LEAVE_OUT ? null : value;
                            if (row.garment) onMatch(group, row.storeValue, blankValue, row.garment.key);
                            else onMatch(group, row.storeValue, blankValue);
                            setEditing((current) => {
                              const next = new Set(current);
                              next.delete(key);
                              return next;
                            });
                          }}
                        />
                      ) : (
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="break-words">
                            {grouped ? (
                              matched ? optionText(matched, true) : row.blankValue
                            ) : matched ? (
                              <>
                                <span className="font-mono">{matched.code}</span>
                                {matched.name ? ` · ${matched.name}` : ''}
                              </>
                            ) : (
                              <span className="font-mono">{row.blankValue}</span>
                            )}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-3 text-xs motion-reduce:transition-none"
                            data-match-control="matched"
                            aria-describedby={`${cellId}-store`}
                            onClick={() => {
                              focusAfterRender.current = `${cellId}-control`;
                              setEditing((current) => new Set(current).add(key));
                            }}
                          >
                            {t('supply.match.change')}
                          </Button>
                        </span>
                      )}
                    </td>
                  )}
                  {matchColumns && (
                    <td className="py-2 pr-3">
                      {locked && (
                        <span className="flex items-start gap-1 leading-normal text-muted-foreground">
                          <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                          {t('supply.match.syncedLocked')}
                        </span>
                      )}
                      {!locked && !row.pending && !row.noValues && row.how === 'auto' && (
                        <StatusPill tone="on">{t('supply.match.matched')}</StatusPill>
                      )}
                      {!locked && !row.pending && row.how === 'chosen' && (
                        <StatusPill tone="off">{t('supply.match.chosen')}</StatusPill>
                      )}
                      {!locked && !row.pending && row.how === 'left_out' && (
                        <span className="text-muted-foreground">{leftOutStateLabel ?? t('supply.match.leftOut')}</span>
                      )}
                      {unmatched && (
                        <span id={helperId} className="flex items-start gap-1 leading-normal text-amber-700">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                          {group === 'colour' ? t('supply.match.chooseColour') : t('supply.match.chooseSize')}
                        </span>
                      )}
                    </td>
                  )}
                  {designColumn && (
                    <td className="py-2">
                      {group === 'colour' && row.how !== 'left_out' && (
                        <span className="flex flex-col items-start gap-1">
                          <SegmentedControl<'light' | 'dark'>
                            size="sm"
                            ariaLabel={
                              row.storeValue
                                ? `${t('supply.design.label')}: ${row.storeValue}`
                                : t('supply.design.label')
                            }
                            value={dark ? 'dark' : 'light'}
                            disabled={locked || row.designLocked === true}
                            onChange={(value) => onDesignChoice(row.storeValue, value === 'dark')}
                            options={[
                              { value: 'light', label: t('supply.design.light') },
                              { value: 'dark', label: t('supply.design.dark') },
                            ]}
                          />
                          {row.suggestedDark && (
                            <span className="text-xs text-muted-foreground">
                              {t('supply.design.suggested', { owner: ownerName })}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        );
      })}
    </table>
  );
}

export interface ValuePickerProps {
  id: string;
  labelledBy: string;
  options: BlankValueOption[];
  /** A blank code, the leave-out sentinel, or null for nothing chosen yet. */
  value: string | null;
  /** The last option's words. Omitted (BLU-1522 S8, the garment picker): no leave-out option. */
  leaveOutLabel?: string;
  placeholder: string;
  invalid: boolean;
  describedBy?: string;
  /** The table's Enter walk reads it (`data-match-control`); omitted outside the table. */
  state?: BlankValueMatchHow;
  /** Value names only, no codes (grouped mode). */
  namesOnly?: boolean;
  /** Default true (`aria-required`). */
  required?: boolean;
  disabled?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  onChange: (value: string) => void;
}

/**
 * A pick from a fixed list (the blank's own values, D-15; BLU-1522 S8 the
 * org's own product type codes); never free text. A Select up to 20 options,
 * a searchable Combobox above that (DESIGN.md §10).
 */
export function ValuePicker({
  id,
  labelledBy,
  options,
  value,
  leaveOutLabel,
  placeholder,
  invalid,
  describedBy,
  state,
  namesOnly = false,
  required = true,
  disabled = false,
  onKeyDown,
  onChange,
}: ValuePickerProps) {
  const [open, setOpen] = useState(false);
  const triggerClass = cn(
    'h-8 w-full max-w-[16rem] text-xs transition-colors duration-200 motion-reduce:transition-none',
    invalid && 'border-amber-300 ring-1 ring-amber-100/50',
  );
  const shared = {
    id,
    'aria-labelledby': labelledBy,
    'aria-required': required ? true : undefined,
    'aria-invalid': invalid,
    'aria-describedby': describedBy,
    'data-match-control': state,
    onKeyDown,
  } as const;

  if (options.length <= SELECT_MAX_OPTIONS) {
    return (
      <Select value={value ?? ''} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger {...shared} className={triggerClass}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              {namesOnly ? (
                optionText(option, true)
              ) : (
                <>
                  <span className="font-mono">{option.code}</span>
                  {option.name ? ` · ${option.name}` : ''}
                </>
              )}
            </SelectItem>
          ))}
          {leaveOutLabel !== undefined && (
            <>
              <SelectSeparator />
              <SelectItem value={LEAVE_OUT}>{leaveOutLabel}</SelectItem>
            </>
          )}
        </SelectContent>
      </Select>
    );
  }

  const chosen = value === LEAVE_OUT && leaveOutLabel !== undefined ? leaveOutLabel : options.find((o) => o.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          {...shared}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(triggerClass, 'justify-between gap-2 px-3 font-normal')}
        >
          <span className={cn('truncate', !chosen && 'text-muted-foreground')}>
            {typeof chosen === 'string' ? chosen : chosen ? optionText(chosen, namesOnly) : placeholder}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 max-w-[calc(100vw-2rem)] p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.code}
                  value={optionText(option)}
                  onSelect={() => {
                    onChange(option.code);
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 text-threadheads-purple',
                      value === option.code ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate text-xs">
                    {namesOnly ? (
                      optionText(option, true)
                    ) : (
                      <>
                        <span className="font-mono">{option.code}</span>
                        {option.name ? ` · ${option.name}` : ''}
                      </>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {leaveOutLabel !== undefined && (
              <>
                <CommandSeparator alwaysRender />
                <CommandGroup forceMount>
                  <CommandItem
                    forceMount
                    value={LEAVE_OUT}
                    onSelect={() => {
                      onChange(LEAVE_OUT);
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <Check
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 text-threadheads-purple',
                        value === LEAVE_OUT ? 'opacity-100' : 'opacity-0',
                      )}
                      aria-hidden="true"
                    />
                    <span className="text-xs">{leaveOutLabel}</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
